import React from "react";
import { Link } from "react-router-dom";
import MenuBar from "../components/MenuBar";
import FooterSection from "../components/FooterSection";
import PageSEO from "../components/PageSEO";
import "../styles/LifecycleMarketingService.css";

export default function LifecycleMarketingPage() {
  return (
    <div className="lifecycle-page">
      <PageSEO
        title="Lifecycle Marketing Services | Retention & Customer LTV | Sparkdenz"
        description="Improve retention and customer lifetime value with Sparkdenz lifecycle marketing services including segmentation, email automation, loyalty, and churn reduction."
        canonicalPath="/lifecycle-marketing"
      />
      <MenuBar />
      <main>
        {/* Hero Section */}
        <section className="service-hero">
          <div className="section-inner">
            <p className="service-tag">Lifecycle Marketing</p>
            <h1>Keep Customers Engaged, Loyal, and Buying Again</h1>
            <p className="service-hero-text">
              Acquisition is only the beginning. We build comprehensive lifecycle marketing 
              programs that transform one-time buyers into repeat customers and brand advocates 
              using strategic nurturing, personalization, and retention tactics.
            </p>
            <div className="service-hero-actions">
              <Link className="btn primary" to="/contact-us">
                Build Retention Strategy
              </Link>
              <Link className="btn ghost" to="/contact-us">
                Schedule Call
              </Link>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="service-details">
          <div className="section-inner">
            <h2>Lifecycle Marketing Services</h2>
            <div className="services-grid">
              <div className="service-item">
                <div className="service-icon">🎯</div>
                <h3>Customer Segmentation</h3>
                <p>
                  One message doesn't fit all. We segment your customer base by behavior, purchase 
                  history, engagement level, and demographics to deliver targeted messaging that 
                  resonates with each group.
                </p>
                <ul className="service-list">
                  <li>Behavioral segmentation</li>
                  <li>RFM analysis</li>
                  <li>Cohort analysis</li>
                  <li>Lifetime value segments</li>
                  <li>Risk-based segmentation</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">✉️</div>
                <h3>Email Marketing & Automation</h3>
                <p>
                  Email remains the highest ROI channel. We build sophisticated email sequences 
                  triggered by customer behavior, personalizing each touchpoint to guide them 
                  through their journey.
                </p>
                <ul className="service-list">
                  <li>Welcome sequences</li>
                  <li>Abandonment flows</li>
                  <li>Win-back campaigns</li>
                  <li>Triggered automations</li>
                  <li>Segmented newsletters</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">🎁</div>
                <h3>Loyalty & Rewards Programs</h3>
                <p>
                  Incentivize repeat purchases and advocacy. We design and implement loyalty 
                  programs that reward customers for purchases, referrals, and engagement, 
                  increasing lifetime value and reducing churn.
                </p>
                <ul className="service-list">
                  <li>Loyalty program design</li>
                  <li>Points & tier structure</li>
                  <li>Referral programs</li>
                  <li>VIP customer tiers</li>
                  <li>Gamification elements</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">📲</div>
                <h3>SMS & Push Notifications</h3>
                <p>
                  Reach customers on their phones with timely, relevant messages. SMS and push 
                  notifications deliver high engagement rates when used strategically throughout 
                  the customer lifecycle.
                </p>
                <ul className="service-list">
                  <li>SMS campaign management</li>
                  <li>Push notification strategy</li>
                  <li>In-app messaging</li>
                  <li>Personalized notifications</li>
                  <li>Timely offer delivery</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">💬</div>
                <h3>Customer Retention & Churn Prevention</h3>
                <p>
                  Losing customers is expensive. We identify at-risk customers before they churn, 
                  implementing proactive retention strategies that address pain points and increase 
                  loyalty.
                </p>
                <ul className="service-list">
                  <li>Churn prediction modeling</li>
                  <li>Win-back campaigns</li>
                  <li>Customer success touchpoints</li>
                  <li>Support escalation flows</li>
                  <li>Satisfaction monitoring</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">📊</div>
                <h3>Analytics & CLV Optimization</h3>
                <p>
                  Maximize customer lifetime value with data-driven insights. We track retention 
                  metrics, repeat purchase rates, and CLV, identifying opportunities to extend 
                  customer relationships and increase profitability.
                </p>
                <ul className="service-list">
                  <li>CLV calculation</li>
                  <li>Retention rate tracking</li>
                  <li>Repeat purchase analysis</li>
                  <li>Cohort performance analysis</li>
                  <li>Revenue expansion reporting</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="service-results">
          <div className="section-inner">
            <h2>Lifecycle Marketing Results</h2>
            <div className="results-grid">
              <div className="result-card">
                <div className="result-stat">38%</div>
                <p>Average repeat purchase increase</p>
              </div>
              <div className="result-card">
                <div className="result-stat">52%</div>
                <p>Average customer retention improvement</p>
              </div>
              <div className="result-card">
                <div className="result-stat">$847K</div>
                <p>Average annual CLV increase</p>
              </div>
              <div className="result-card">
                <div className="result-stat">3.2x</div>
                <p>Average email ROI achieved</p>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="service-process">
          <div className="section-inner">
            <h2>Our Lifecycle Approach</h2>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">01</div>
                <h3>Define Customer Journey</h3>
                <p>Map the complete customer lifecycle from acquisition through advocacy.</p>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">02</div>
                <h3>Build Segmentation</h3>
                <p>Create customer segments based on behavior, value, and lifecycle stage.</p>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">03</div>
                <h3>Design Programs</h3>
                <p>Develop messaging, email flows, and retention programs for each segment.</p>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">04</div>
                <h3>Optimize & Scale</h3>
                <p>Monitor performance, optimize campaigns, and scale winning strategies.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="service-cta">
          <div className="section-inner">
            <h2>Build Your Lifetime Customer Strategy</h2>
            <p>Let's create a lifecycle marketing program that dramatically increases customer loyalty and revenue.</p>
            <div className="cta-buttons">
              <Link className="btn primary" to="/contact-us">
                Build Strategy
              </Link>
              <Link className="btn ghost" to="/contact-us">
                Speak with Expert
              </Link>
            </div>
          </div>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}
